import { gql, useMutation, useQuery, useReactiveVar } from '@apollo/client';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Button, CircularProgress, Container, CssBaseline, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { useParams } from '@tanstack/react-router';
import { useFormik } from 'formik';
import { jsonToGraphQLQuery } from 'json-to-graphql-query';
import React, { type MouseEvent, useEffect, useState } from 'react';
import allViews from 'src/api/viewModels/allViews';
import SharedItemDownloadModal from 'src/components/Modals/SharedItemDownloadModal';
import NotFound from 'src/components/NotFound/NotFound';
import { basicSharedLinkInfo, sharedLinkMetadata } from 'src/dynamoQueries/sharedLinks';
import TopNavBar from 'src/HomePage/TopNavBar/TopNavBar';
import { generateLibraryViewModels } from 'src/helpers/generateViewModels';
import MediaPage from 'src/Media/MediaPage';
import styles from 'src/SharedLinkPage/SharedLinkContentStyles';
import viewStateVar from 'src/store/viewState';
import type { ViewNode } from 'src/types/store.types';
import * as Yup from 'yup';
import { SharedLinkContext, type SharedLinkInfo } from './context';
import SharedLinkCollectionSwitchComponent from './SharedLinkCollectionPage/SharedCollectionSwitch';

interface PasswordFormValues {
  password: string;
}

interface BasicSharedLinkInfoData {
  basicSharedLinkInfo: {
    expired: boolean;
    password: boolean;
  };
}

interface SharedLinkMetadataResponse {
  accessSharedLinkMetadata: {
    sharedLinkMetadata: SharedLinkInfo;
  };
}

interface AllViewsData {
  allViews: {
    edges: ViewNode[];
  };
}

interface SharedLinkContentProps {
  mediaPage?: boolean;
}

const SharedLinkContent = ({ mediaPage }: SharedLinkContentProps): React.JSX.Element | null => {
  const params = useParams({ strict: false });
  const linkId = (params as { linkId?: string }).linkId;
  const classes = styles();
  const viewState = useReactiveVar(viewStateVar);

  // State declarations must come before hooks that reference them
  const [expired, setExpired] = useState(false);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [authorized, setAuthorized] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [linkMetadata, setLinkMetadata] = useState<SharedLinkInfo | null>(null);

  const buildLinkMetadataMutation = (): ReturnType<typeof gql> => {
    const mutation = sharedLinkMetadata();
    return gql(jsonToGraphQLQuery(mutation, { pretty: true }));
  };

  const buildBasicInfoQuery = (): ReturnType<typeof gql> => {
    const query = basicSharedLinkInfo();
    return gql(jsonToGraphQLQuery(query, { pretty: true }));
  };

  const [sharedLinkData] = useMutation<SharedLinkMetadataResponse>(buildLinkMetadataMutation());

  // Query allViews only after authorized - the mutation sets the cookie,
  // and skip ensures we don't query until the cookie is ready
  const allViewsQuery = useQuery<AllViewsData>(allViews, {
    variables: { viewName: 'Library' },
    fetchPolicy: 'network-only',
    skip: !authorized,
  });

  const basicInfoQuery = useQuery<BasicSharedLinkInfoData>(buildBasicInfoQuery(), {
    variables: { linkId: linkId },
  });

  const formik = useFormik<PasswordFormValues>({
    initialValues: {
      password: '',
    },
    validationSchema: Yup.object({
      password: Yup.string().required('This field is required'),
    }),
    onSubmit: async (values: PasswordFormValues): Promise<void> => {
      const { password } = values;
      sharedLinkData({
        variables: { linkId, password },
      })
        .then((resp) => {
          if (resp.data) {
            setLinkMetadata(resp.data.accessSharedLinkMetadata.sharedLinkMetadata);
            setAuthorized(true);
            // Setting authorized=true triggers allViewsQuery (skip: !authorized)
          }
        })
        .catch((e: Error) => {
          handlePasswordError(e);
        });
    },
  });

  useEffect(() => {
    if (basicInfoQuery.data) {
      if (basicInfoQuery.data.basicSharedLinkInfo.expired) setExpired(true);
      else if (basicInfoQuery.data.basicSharedLinkInfo.password) setRequiresPassword(true);
      else {
        sharedLinkData({
          variables: { linkId: linkId },
        })
          .then((resp) => {
            if (resp.data) {
              setLinkMetadata(resp.data.accessSharedLinkMetadata.sharedLinkMetadata);
              setAuthorized(true);
              // Setting authorized=true triggers allViewsQuery (skip: !authorized)
            }
          })
          .catch((e: Error) => {
            setLinkError(e.message);
          });
      }
    }
  }, [basicInfoQuery.data, linkId, sharedLinkData]);

  useEffect(() => {
    allViewsQuery?.data && generateLibraryViewModels(allViewsQuery?.data);
  }, [allViewsQuery?.data]);

  useEffect(() => {
    if (basicInfoQuery.error) setLinkError(basicInfoQuery.error.message);
    if (expired) setLinkError('This link has expired');
  }, [basicInfoQuery.error, expired]);

  const handlePasswordError = (e: Error): void => {
    formik.setFieldError('password', e.message);
  };

  const togglePasswordVisibility = (): void => {
    setPasswordVisibility(!passwordVisibility);
  };

  const handleMouseDownPassword = (event: MouseEvent<HTMLButtonElement>): void => {
    event.preventDefault();
  };

  // Show loading spinner while queries are in progress
  // Also show loading if authorized but allViewsQuery hasn't completed yet
  if (basicInfoQuery.loading || allViewsQuery.loading || (authorized && !allViewsQuery.data && !allViewsQuery.error)) {
    return (
      <React.Fragment>
        <TopNavBar simple />
        <Box display="flex" alignItems="center" justifyContent="center" height={'75vh'} width={'100%'}>
          <CircularProgress color="secondary" />
        </Box>
      </React.Fragment>
    );
  }

  if (linkError || allViewsQuery.error) {
    console.error('SharedLink error:', linkError || allViewsQuery.error);
    return <NotFound />;
  }

  if (requiresPassword && !authorized) {
    return (
      <Container className={classes.formContainer} component="main">
        <CssBaseline />
        <form className={classes.form} onSubmit={formik.handleSubmit}>
          <Typography component="h1" variant="h1" className={classes.formTitle}>
            Password Required
          </Typography>
          <div className={classes.formElements}>
            <TextField
              label="Password"
              type={passwordVisibility ? 'text' : 'password'}
              variant="outlined"
              className={classes.formInput}
              fullWidth
              {...formik.getFieldProps('password')}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              FormHelperTextProps={{
                classes: {
                  root: classes.helperText,
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={togglePasswordVisibility}
                      onMouseDown={handleMouseDownPassword}
                      edge="end"
                      size="large"
                    >
                      {passwordVisibility ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={Boolean(formik.errors.password)}
              className={classes.submitButton}
            >
              SUBMIT
            </Button>
          </div>
        </form>
      </Container>
    );
  }

  if (authorized && linkMetadata && allViewsQuery.data) {
    if (linkMetadata.assetId) {
      return (
        <>
          <TopNavBar simple currentPage={undefined} />
          <SharedLinkContext.Provider value={{ linkInfo: linkMetadata, linkId: linkId ?? '' }}>
            <MediaPage isShared={true} itemId={linkMetadata.assetId} />
            {/* Shared item download modal */}
            {!!viewState.sharedItemDownloadDialog && <SharedItemDownloadModal />}
          </SharedLinkContext.Provider>
        </>
      );
    }

    if (linkMetadata.collectionId) {
      return (
        <SharedLinkContext.Provider value={{ linkInfo: linkMetadata, linkId: linkId ?? '' }}>
          <SharedLinkCollectionSwitchComponent mediaPage={mediaPage} />
          {/* Shared item download modal (for items within a shared collection) */}
          {!!viewState.sharedItemDownloadDialog && <SharedItemDownloadModal />}
        </SharedLinkContext.Provider>
      );
    }
  }

  return null;
};

export default SharedLinkContent;
